<template>
  <div class="search-bar" :class="{ collapsed: isCollapsed }">
    <div
      :style="{ backgroundColor: backgroundColor }"
      class="search-bar-inner"
      v-show="!isCollapsed"
    >
      <!-- Filter Section -->
      <section class="sidebar-section">
        <h2 class="title-primary">
          <el-icon class="title-primary-icon">
            <Filter />
          </el-icon>
          <span class="title-primary-text">検索情報</span>
        </h2>

        <el-form
          gap="100px"
          :model="filterForm"
          label-width="0"
          class="filter-form"
        >
          <!-- Ship Date Picker -->
          <div class="form-field">
            <label class="field-label">出荷日 開始</label>
            <el-date-picker
              :model-value="filterForm.shipStartDate"
              type="date"
              placeholder="日付を選択"
              style="width: 100%"
              format="YYYY-MM-DD"
              value-format="x"
              @update:model-value="handleShipStartDateChange"
            />
          </div>

          <div class="form-field">
            <label class="field-label">出荷日 終了</label>
            <el-date-picker
              :model-value="filterForm.shipEndDate"
              type="date"
              placeholder="日付を選択"
              style="width: 100%"
              format="YYYY-MM-DD"
              value-format="x"
              @update:model-value="handleShipEndDateChange"
            />
          </div>

          <!-- Business Type Section (with highlight) -->
          <div class="form-field">
            <label class="field-label">事業区分</label>
            <el-checkbox-group
              :model-value="filterForm.businessTypes"
              @change="handleBusinessTypesChange"
            >
              <div class="checkbox-group">
                <el-checkbox
                  v-for="businessType in businessTypeList"
                  :key="businessType.id"
                  :label="businessType.id"
                >
                  {{ businessType.label }}
                </el-checkbox>
              </div>
            </el-checkbox-group>
          </div>

          <!-- Status Section -->
          <div class="form-field">
            <label class="field-label">ステータス</label>
            <el-checkbox-group
              :model-value="filterForm.status"
              @change="handleStatusChange"
            >
              <div class="checkbox-group">
                <el-checkbox label="依頼" />
                <el-checkbox label="準備中" />
                <el-checkbox label="完了" />
              </div>
            </el-checkbox-group>
          </div>

          <!-- Ship Type Section -->
          <div class="form-field">
            <label class="field-label">出荷種別</label>
            <el-checkbox-group
              :model-value="filterForm.shipType"
              @change="handleShipTypeChange"
            >
              <div class="checkbox-group">
                <el-checkbox label="出荷 " />
                <el-checkbox label="出荷(確定)" />
                <el-checkbox label="バルク" />
              </div>
            </el-checkbox-group>
          </div>

          <!-- Approval Section -->
          <div class="form-field">
            <label class="field-label">出荷承認</label>
            <el-checkbox-group
              :model-value="filterForm.approveTypes"
              @change="handleApproveTypesChange"
            >
              <div class="checkbox-group">
                <el-checkbox label="済" />
              </div>
            </el-checkbox-group>
          </div>
        </el-form>
      </section>

      <!-- Footer Buttons -->
      <div class="sidebar-footer">
        <el-button @click="handleClose" class="footer-button">
          閉じる
        </el-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { Filter } from '@element-plus/icons-vue';
  import type { ShipFilterData } from '@/types/ship';
  import type { BusinessTypesDataState } from '@/types/master/businessType';
  import { getEnv } from '@/libs/env';

  const props = defineProps<{
    filterForm: ShipFilterData;
    businessTypeList: BusinessTypesDataState[];
    isCollapsed?: boolean;
  }>();

  const backgroundColor = getEnv() === 'staging' ? '#f1f1f1' : '#e7effb';

  const emit = defineEmits<{
    'update:filterForm': [value: ShipFilterData];
    filter: [];
    'filter-status': [];
    'filter-approve': [];
    close: [];
  }>();

  const handleShipStartDateChange = (value: number | null) => {
    emit('update:filterForm', { ...props.filterForm, shipStartDate: value });
    emit('filter');
  };

  const handleShipEndDateChange = (value: number | null) => {
    emit('update:filterForm', { ...props.filterForm, shipEndDate: value });
    emit('filter');
  };

  const handleBusinessTypesChange = (value: number[]) => {
    emit('update:filterForm', { ...props.filterForm, businessTypes: value });
    emit('filter');
  };

  const handleStatusChange = (value: string[]) => {
    emit('update:filterForm', { ...props.filterForm, status: value });
    emit('filter-status');
  };

  const handleShipTypeChange = (value: string[]) => {
    emit('update:filterForm', { ...props.filterForm, shipType: value });
    emit('filter');
  };

  const handleApproveTypesChange = (value: string[]) => {
    emit('update:filterForm', { ...props.filterForm, approveTypes: value });
    emit('filter-approve');
  };

  const handleClose = () => {
    emit('close');
  };
</script>

<style scoped lang="scss">
  .search-bar {
    position: relative;
    width: 300px;
    height: 100%;
    box-shadow: 0 2px 4px 0 rgb(0 0 0 / 25%);
    overflow: visible;
    transition: width 0.4s ease;

    &.collapsed {
      width: 0;
      overflow: visible;
    }

    &:hover {
      .sidebar-toggle {
        opacity: 1;
      }
    }
  }

  .search-bar-inner {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .sidebar-section {
    margin: 0 16px;
    font-size: 14px;
  }

  // Title
  .title-primary {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
  }

  .title-primary-icon {
    display: inline-flex;
    margin-right: 4px;
    font-size: 20px;
    color: #606266;
  }

  .title-primary-text {
    font-weight: bold;
    font-size: 18px;
    color: #303133;
  }

  .filter-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .form-field {
    display: flex;
    flex-direction: column;
    gap: 8px;

    &:not(:last-child) {
      margin-bottom: 16px;
    }
  }

  .field-label {
    font-size: 15px;
    font-weight: 600;
    color: #606266;
    text-align: left;
  }

  .highlight-section {
    background-color: #fffbe6;
    padding: 16px;
    border-radius: 6px;
    border: 1px solid #ffe58f;
  }

  .checkbox-group {
    display: flex;
    gap: 10px;
    width: 100%;
    flex-wrap: wrap;
    justify-content: space-between;

    &:deep(.el-checkbox) {
      flex: 1;
    }
  }

  .el-checkbox {
    margin-right: 0;
    font-weight: 500;
    font-size: 13px;
    :deep(.el-checkbox__label) {
      font-size: 13px;
      color: #606266;
    }
  }

  .el-input {
    width: 100%;
  }

  .sidebar-footer {
    display: flex;
    justify-content: space-between;
    padding: 16px;
    margin-top: auto;
  }

  .footer-button {
    flex: 1;
  }

  /* Scrollbar styling */
  .search-bar-inner::-webkit-scrollbar {
    width: 6px;
  }

  .search-bar-inner::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  .search-bar-inner::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  .search-bar-inner::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
</style>
